"use client";

import { ECookieKeys } from "@/const/cookie-keys.const";
import type { MenuProps } from "antd";
import { Button, Dropdown, Layout, Menu, Drawer, theme } from "antd";
import Cookies from "js-cookie";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import styles from "./styles.module.scss";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useMemo } from "react";
import { useCallback } from "react";
import GetLayoutContainerContext from "@/contexts/get-layout-container-context";

const { Header, Content, Sider } = Layout;

const menuItems: MenuProps["items"] = [
  {
    key: "/admin/companies",
    icon: <Image src="/admin/users.svg" alt="" width={20} height={20} />,
    label: "顧客管理",
  },
  {
    key: "/admin/accounts",
    icon: (
      <Image
        src="/admin/manage_accounts.svg"
        alt=""
        className="light:invert"
        width={20}
        height={20}
      />
    ),
    label: "運営アカウント",
  },
  {
    key: "/admin/payment",
    icon: <Image src="/admin/receipt_long.svg" alt="" width={20} height={20} />,
    label: "請求管理",
  },
  {
    key: "/admin/download",
    icon: <Image src="/admin/download.svg" alt="" width={20} height={20} />,
    label: "各会社のCSVダウンロード",
  },
  {
    key: "/admin/company-csv",
    icon: <Image src="/admin/download.svg" alt="" width={20} height={20} />,
    label: "CSVダウンロード",
  },
  {
    key: "/admin/integrations/hubspot",
    icon: <Image src="/admin/receipt_long.svg" alt="" width={20} height={20} />,
    label: "HubSpot 連携",
  },
];

const AdminLayout: React.FC = ({ children }: { children: React.ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    const token = Cookies.get(ECookieKeys.TOKEN_ADMIN);
    if (!token) {
      router.replace("/admin/login");
      return;
    }
  }, [router]);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const signOut = useCallback(() => {
    Cookies.remove(ECookieKeys.TOKEN_ADMIN);
    router.replace("/admin/login");
  }, [router]);

  const goToProfile = useCallback(
    () => () => {
      router.replace("/admin/profile");
    },
    [router]
  );

  const dropdownItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "1",
        label: (
          <Button
            type="link"
            className="!text-[#666666] !font-bold"
            onClick={goToProfile}
          >
            個人アカウント設定
          </Button>
        ),
      },
      {
        key: "2",
        label: (
          <Button
            type="link"
            className="!text-[#666666] !font-bold"
            onClick={signOut}
          >
            ログアウト
          </Button>
        ),
      },
    ],
    [goToProfile, signOut]
  );

  const handleMenuClick = useCallback(
    (e: { key: string }) => {
      router.replace(e.key);
      if (isMobile) {
        setMobileDrawerOpen(false);
      }
    },
    [router, isMobile]
  );

  const toggleMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  }, [mobileDrawerOpen]);

  const ref = useMemo(() => React.createRef<HTMLDivElement>(), []);

  const renderSidebarContent = useCallback(
    () => (
      <div className="h-full flex flex-col">
        {!isMobile && (
          <div
            className={twMerge(
              "flex w-full p-2",
              collapsed ? "justify-center" : "justify-end"
            )}
          >
            <Button
              type="text"
              className="!bg-white !w-[25px] !h-[25px] !rounded-[2px]"
              icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
          </div>
        )}
        <Menu
          mode="inline"
          defaultSelectedKeys={["1"]}
          defaultOpenKeys={["sub1"]}
          items={menuItems}
          className="!bg-[#2290F1] flex-1 !border-r-0"
          onSelect={handleMenuClick}
        />
      </div>
    ),
    [collapsed, handleMenuClick, isMobile]
  );

  const handleNotificationClick = () => {
    router.replace("/admin/notifications");
  };

  return (
    <Layout ref={ref} className={twMerge(styles.adminLayout, "min-h-screen")}>
      <GetLayoutContainerContext.Provider value={() => ref.current}>
        <Header className="flex items-center justify-between px-4 !h-16">
          <div className="flex items-center gap-3">
            {isMobile && (
              <Button
                type="text"
                icon={
                  <div className="flex flex-col gap-1">
                    <div className="w-5 h-0.5 bg-white"></div>
                    <div className="w-5 h-0.5 bg-white"></div>
                    <div className="w-5 h-0.5 bg-white"></div>
                  </div>
                }
                onClick={toggleMobileDrawer}
                className="!text-white hover:!bg-gray-700"
              />
            )}
            <Image
              src="/logo-admin.svg"
              alt="Vercel logomark"
              width={150}
              height={60}
            />
          </div>
          <div className="flex gap-x-3 px-4">
            <Dropdown menu={{ items: dropdownItems }} placement="bottomLeft">
              <Image
                src="/admin/account_circle.svg"
                alt=""
                width={20}
                height={20}
                className="cursor-pointer"
              />
            </Dropdown>
            <Image src="/admin/settings.svg" alt="" width={20} height={20} />
            <Image
              src="/admin/notifications.svg"
              alt=""
              width={16}
              height={20}
              // onClick={handleNotificationClick}
              className="cursor-pointer"
            />
          </div>
        </Header>

        <Layout style={{ minHeight: "calc(100vh - 64px)" }}>
          {!isMobile && (
            <Sider
              width={200}
              style={{ background: colorBgContainer }}
              collapsed={collapsed}
              className="!bg-[#2290F1]"
            >
              {renderSidebarContent()}
            </Sider>
          )}

          {isMobile && (
            <Drawer
              title={null}
              placement="left"
              onClose={() => setMobileDrawerOpen(false)}
              open={mobileDrawerOpen}
              width={250}
              className="!p-0"
              bodyStyle={{ padding: 0, background: "#2290F1" }}
              headerStyle={{ display: "none" }}
            >
              {renderSidebarContent()}
            </Drawer>
          )}

          <Layout className="px-8">
            <Content className={twMerge("py-8 !pb-24", isMobile && "!py-4")}>
              {children}
            </Content>
          </Layout>
        </Layout>
      </GetLayoutContainerContext.Provider>
    </Layout>
  );
};

export default AdminLayout;
